import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { api, errorText, formatDate, formatDateTime, formatQty } from '../api';
import { Badge, Card, Empty, Field, Loading, Notice, SelectField, TextField } from '../components/ui';

interface KatalogArtikel {
  article_id: string;
  sku: string;
  name: string;
  unit: string;
  ean: string | null;
  verfuegbar: number;
}

type Status = 'offen' | 'kommissionierung' | 'kommissioniert' | 'versendet' | 'storniert';

interface Anforderung {
  id: string;
  nummer: string;
  referenz: string | null;
  status: Status;
  statusText: string;
  wunschtermin: string | null;
  erstelltAm: string;
  versendetAm: string | null;
  trackingRef: string | null;
  positionen: number;
  mengeGesamt: number;
  notiz: string | null;
  fond: string | null;
  lieferung: string | null;
}

interface Fond {
  id: string;
  bezeichnung: string;
  kennung: string | null;
}

interface AnforderungDetail extends Anforderung {
  zeilen: {
    position: number;
    sku: string;
    name: string;
    unit: string;
    mengeAngefordert: number;
    mengeVersendet: number;
    notiz: string | null;
  }[];
}

interface KorbPosten {
  artikel: KatalogArtikel;
  menge: number;
}

const STATUS_STIL: Record<Status, 'info' | 'warn' | 'ok' | 'bad'> = {
  offen: 'info',
  kommissionierung: 'warn',
  kommissioniert: 'warn',
  versendet: 'ok',
  storniert: 'bad',
};

export function PortalPage() {
  const [tab, setTab] = useState<'neu' | 'meine'>('neu');
  const [ownerName, setOwnerName] = useState('');

  useEffect(() => {
    api<{ ownerName: string }>('/api/portal/profil')
      .then((p) => setOwnerName(p.ownerName))
      .catch(() => setOwnerName(''));
  }, []);

  return (
    <div className="grid">
      {ownerName && (
        <Notice kind="info">
          Sie fordern Ware für <strong>{ownerName}</strong> an. Angezeigt wird ausschliesslich Ihr
          eigener, bei uns eingelagerter Bestand.
        </Notice>
      )}

      <div className="tabs">
        <button className={tab === 'neu' ? 'active' : ''} onClick={() => setTab('neu')}>
          Artikel anfordern
        </button>
        <button className={tab === 'meine' ? 'active' : ''} onClick={() => setTab('meine')}>
          Meine Anforderungen
        </button>
      </div>

      {tab === 'neu' ? (
        <NeueAnforderung onGesendet={() => setTab('meine')} />
      ) : (
        <MeineAnforderungen />
      )}
    </div>
  );
}

function NeueAnforderung({ onGesendet }: { onGesendet: () => void }) {
  const [suche, setSuche] = useState('');
  const [katalog, setKatalog] = useState<KatalogArtikel[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [korb, setKorb] = useState<Record<string, KorbPosten>>({});
  const [lieferFirma, setLieferFirma] = useState('');
  const [lieferVorname, setLieferVorname] = useState('');
  const [lieferNachname, setLieferNachname] = useState('');
  const [lieferStrasse, setLieferStrasse] = useState('');
  const [lieferPlz, setLieferPlz] = useState('');
  const [lieferOrt, setLieferOrt] = useState('');
  const [lieferInfo, setLieferInfo] = useState('');
  const [wunschtermin, setWunschtermin] = useState('');
  const [notiz, setNotiz] = useState('');
  const [fonds, setFonds] = useState<Fond[]>([]);
  const [fondId, setFondId] = useState('');
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Fond[]>('/api/portal/fonds')
      .then(setFonds)
      .catch(() => setFonds([]));
  }, []);

  useEffect(() => {
    let aktiv = true;
    setLaedt(true);
    const t = setTimeout(() => {
      api<KatalogArtikel[]>('/api/portal/katalog', { query: { search: suche || undefined } })
        .then((rows) => aktiv && setKatalog(rows))
        .catch((e) => aktiv && setFehler(errorText(e)))
        .finally(() => aktiv && setLaedt(false));
    }, 300);
    return () => {
      aktiv = false;
      clearTimeout(t);
    };
  }, [suche]);

  const setMenge = useCallback((artikel: KatalogArtikel, wert: string) => {
    const menge = wert === '' ? 0 : Number(wert);
    setKorb((alt) => {
      const neu = { ...alt };
      if (!menge || menge <= 0) delete neu[artikel.article_id];
      else neu[artikel.article_id] = { artikel, menge };
      return neu;
    });
  }, []);

  const posten = useMemo(() => Object.values(korb), [korb]);

  async function absenden() {
    setFehler('');
    if (!posten.length) {
      setFehler('Bitte mindestens einen Artikel mit Menge angeben.');
      return;
    }
    if (fonds.length > 0 && !fondId) {
      setFehler('Bitte einen Fond (Abteilung) für die Abrechnung wählen.');
      return;
    }
    setBusy(true);
    try {
      await api('/api/portal/anforderungen', {
        method: 'POST',
        body: {
          lieferFirma: lieferFirma.trim() || null,
          lieferVorname: lieferVorname.trim() || null,
          lieferNachname: lieferNachname.trim() || null,
          lieferStrasse: lieferStrasse.trim() || null,
          lieferPlz: lieferPlz.trim() || null,
          lieferOrt: lieferOrt.trim() || null,
          lieferInfo: lieferInfo.trim() || null,
          wunschtermin: wunschtermin || null,
          notiz: notiz.trim() || null,
          fondId: fondId || null,
          positionen: posten.map((p) => ({ articleId: p.artikel.article_id, menge: p.menge })),
        },
      });
      setKorb({});
      setLieferFirma('');
      setLieferVorname('');
      setLieferNachname('');
      setLieferStrasse('');
      setLieferPlz('');
      setLieferOrt('');
      setLieferInfo('');
      setWunschtermin('');
      setNotiz('');
      setFondId('');
      onGesendet();
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid two">
      <Card title="Katalog" actions={<span className="small muted">nur Ihre Artikel</span>}>
        <TextField
          label="Suche"
          value={suche}
          onChange={setSuche}
          placeholder="Artikelname, Nummer oder EAN"
        />
        {laedt ? (
          <Loading />
        ) : katalog.length === 0 ? (
          <Empty>Keine Artikel gefunden.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Artikel</th>
                <th className="num">Verfügbar</th>
                <th className="num" style={{ width: 130 }}>
                  Menge
                </th>
              </tr>
            </thead>
            <tbody>
              {katalog.map((a) => (
                <tr key={a.article_id}>
                  <td>
                    <strong>{a.name}</strong>
                    <div className="small muted">
                      {a.sku}
                      {a.ean ? ` · ${a.ean}` : ''}
                    </div>
                  </td>
                  <td className="num">{formatQty(a.verfuegbar, a.unit)}</td>
                  <td className="num">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      style={{ width: 110, textAlign: 'right' }}
                      value={korb[a.article_id]?.menge ?? ''}
                      onChange={(e) => setMenge(a, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title={`Anforderung (${posten.length})`}>
        {fehler && <Notice kind="error">{fehler}</Notice>}
        {posten.length === 0 ? (
          <Empty>Noch keine Positionen. Menge im Katalog eintragen.</Empty>
        ) : (
          <table>
            <tbody>
              {posten.map((p) => (
                <tr key={p.artikel.article_id}>
                  <td>
                    <strong>{p.artikel.name}</strong>
                    <div className="small muted">{p.artikel.sku}</div>
                  </td>
                  <td className="num">{formatQty(p.menge, p.artikel.unit)}</td>
                  <td className="num">
                    <button className="small" onClick={() => setMenge(p.artikel, '')}>
                      entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {fonds.length > 0 && (
          <SelectField
            label="Fond (Abteilung zur Abrechnung)"
            value={fondId}
            onChange={setFondId}
            options={fonds}
            render={(f) => (f.kennung ? `${f.kennung} — ${f.bezeichnung}` : f.bezeichnung)}
          />
        )}
        <h4 style={{ margin: '16px 0 4px' }}>Lieferanschrift</h4>
        <TextField label="Firma (optional)" value={lieferFirma} onChange={setLieferFirma} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <TextField label="Vorname" value={lieferVorname} onChange={setLieferVorname} />
          <TextField label="Nachname" value={lieferNachname} onChange={setLieferNachname} />
        </div>
        <TextField
          label="Straße und Hausnummer"
          value={lieferStrasse}
          onChange={setLieferStrasse}
          placeholder="z. B. Wilhelm-Buck-Str. 4"
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <TextField label="PLZ" value={lieferPlz} onChange={setLieferPlz} />
          <TextField label="Ort" value={lieferOrt} onChange={setLieferOrt} />
        </div>
        <TextField
          label="Zusätzliche Info (optional)"
          value={lieferInfo}
          onChange={setLieferInfo}
          placeholder="z. B. Zimmer 204, Aktenzeichen, Ansprechpartner"
        />
        <TextField label="Wunschtermin" type="date" value={wunschtermin} onChange={setWunschtermin} />
        <Field label="Notiz">
          <textarea
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            rows={5}
            placeholder="optional – Hinweise zur Anforderung"
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 'inherit', padding: 8 }}
          />
        </Field>

        <button className="primary" disabled={busy || posten.length === 0} onClick={absenden}>
          {busy ? 'Wird gesendet …' : 'Anforderung absenden'}
        </button>
      </Card>
    </div>
  );
}

function MeineAnforderungen() {
  const [liste, setListe] = useState<Anforderung[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [offen, setOffen] = useState<string | null>(null);
  const [detail, setDetail] = useState<AnforderungDetail | null>(null);

  const laden = useCallback(() => {
    api<Anforderung[]>('/api/portal/anforderungen')
      .then(setListe)
      .catch((e) => setFehler(errorText(e)));
  }, []);

  useEffect(() => laden(), [laden]);

  function umschalten(id: string) {
    if (offen === id) {
      setOffen(null);
      setDetail(null);
      return;
    }
    setOffen(id);
    setDetail(null);
    api<AnforderungDetail>(`/api/portal/anforderungen/${id}`)
      .then(setDetail)
      .catch((e) => setFehler(errorText(e)));
  }

  if (fehler) return <Notice kind="error">{fehler}</Notice>;
  if (!liste) return <Loading />;
  if (liste.length === 0) return <Empty>Sie haben noch keine Anforderung gestellt.</Empty>;

  return (
    <Card title="Meine Anforderungen" padded={false}>
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Nummer</th>
            <th>Bezug</th>
            <th>Status</th>
            <th className="num">Positionen</th>
            <th>Eingegangen</th>
            <th>Versendet</th>
          </tr>
        </thead>
        <tbody>
          {liste.map((a) => (
            <Fragment key={a.id}>
              <tr onClick={() => umschalten(a.id)} style={{ cursor: 'pointer' }}>
                <td>
                  <strong>{a.nummer}</strong>
                </td>
                <td>{a.referenz || '—'}</td>
                <td>
                  <Badge kind={STATUS_STIL[a.status]}>{a.statusText}</Badge>
                </td>
                <td className="num">{a.positionen}</td>
                <td>{formatDateTime(a.erstelltAm)}</td>
                <td>{a.versendetAm ? formatDateTime(a.versendetAm) : '—'}</td>
              </tr>
              {offen === a.id && (
                <tr>
                  <td colSpan={6} style={{ background: 'var(--surface-2, #f6f7f9)' }}>
                    {!detail ? (
                      <Loading />
                    ) : (
                      <div className="stack" style={{ padding: 8 }}>
                        {detail.fond && <div className="small">Fond: {detail.fond}</div>}
                        {detail.lieferung && (
                          <div className="small">
                            Lieferanschrift:
                            <br />
                            {detail.lieferung.split('\n').map((z, i) => (
                              <span key={i}>
                                {z}
                                <br />
                              </span>
                            ))}
                          </div>
                        )}
                        {detail.wunschtermin && (
                          <div className="small muted">
                            Wunschtermin: {formatDate(detail.wunschtermin)}
                          </div>
                        )}
                        {detail.notiz && <div className="small">Notiz: {detail.notiz}</div>}
                        {detail.trackingRef && (
                          <div className="small">Sendungsverfolgung: {detail.trackingRef}</div>
                        )}
                        <table>
                          <thead>
                            <tr>
                              <th>Artikel</th>
                              <th className="num">Angefordert</th>
                              <th className="num">Versendet</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.zeilen.map((z) => (
                              <tr key={z.position}>
                                <td>
                                  <strong>{z.name}</strong>
                                  <div className="small muted">{z.sku}</div>
                                </td>
                                <td className="num">{formatQty(z.mengeAngefordert, z.unit)}</td>
                                <td className="num">{formatQty(z.mengeVersendet, z.unit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </Card>
  );
}
